/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { BillingDetails } from '@modules/billing-api/customTypes'
import { BillingModel, PartnerSelling } from '@modules/ui-types/billing'

// eslint-disable-next-line import/no-restricted-paths
import { awsBillingUrl, azureBillingUrl, gcpBillingUrl } from '@/apps/userconsole/urls'

import { MARKETPLACE_CHANNELS } from './constants'

export function isPrepaidConsumptionCustomer({ billing_model }: BillingDetails): boolean {
  return billing_model === BillingModel.PrepaidConsumption
}

export function isPrepaidConsumptionDirectCustomer({
  billing_model,
  channel,
}: BillingDetails): boolean {
  return billing_model === BillingModel.PrepaidConsumption && channel === 'direct'
}

export function isPrepaidConsumptionDirectResellerCustomer({
  billing_model,
  channel,
  partner_selling,
}: BillingDetails): boolean {
  return (
    billing_model === BillingModel.PrepaidConsumption &&
    channel === 'direct' &&
    partner_selling === PartnerSelling.Reseller
  )
}

export function isMarketplaceCustomer({ channel }: Partial<BillingDetails>): boolean {
  if (!channel) {
    return false
  }

  return MARKETPLACE_CHANNELS.includes(channel)
}

export function isAnnualAndOverages({ billing_model }: BillingDetails): boolean {
  return billing_model === 'annual_overages' || billing_model === 'annual'
}

export function isDrawdown({ billing_model }: BillingDetails): boolean {
  return billing_model === 'drawdown'
}

export function isMonthly({ billing_model }: BillingDetails): boolean {
  return billing_model === 'monthly'
}

export function isInactive({ billing_model }: BillingDetails): boolean {
  return billing_model === 'none'
}

export const getMarketplaceDisplayName = (channel?: BillingDetails['channel']): string => {
  if (!channel) {
    return ''
  }

  return {
    gcp: 'GCP',
    azure: 'Azure',
    aws: 'AWS',
  }[channel]
}

export const getMarketplaceBillingLink = (channel?: BillingDetails['channel']): string => {
  if (!channel) {
    return ''
  }

  return {
    aws: awsBillingUrl,
    gcp: gcpBillingUrl,
    azure: azureBillingUrl,
  }[channel]
}

export function isTrial({ billing_model, channel, trial }: BillingDetails): boolean {
  // AWS has this odd trial period and we cannot relay on the billing model as for any other customers
  if (isMarketplaceCustomer({ channel })) {
    if (!trial.state) {
      return false
    }

    // MP trial cannot be expired and not started
    return ['in_trial', 'in_trial_extended'].includes(trial.state)
  }

  return billing_model === 'trial'
}

// ts-unused-exports:disable-next-line
export function isActiveTrial({ trial, billing_model }: BillingDetails): boolean {
  if (!trial.state) {
    return false
  }

  return billing_model === 'trial' && ['in_trial', 'in_trial_extended'].includes(trial.state)
}

export function isActiveTrialNotExtended({ trial, billing_model }: BillingDetails): boolean {
  if (!trial.state) {
    return false
  }

  return billing_model === 'trial' && trial.state === 'in_trial'
}

// ts-unused-exports:disable-next-line
export function isTrialNotStarted({ trial, billing_model }: BillingDetails): boolean {
  if (!trial.state) {
    return false
  }

  return billing_model === 'trial' && ['eligible', 'ineligible'].includes(trial.state)
}

export function isExpiredTrial({ trial, billing_model }: BillingDetails): boolean {
  return billing_model === 'trial' && trial.state === 'expired'
}

export function isConsumptionProcessing({ billing_processing }: BillingDetails): boolean {
  return billing_processing === 'consumption'
}

interface ThirdPartyIntegrationContext {
  billing_model: BillingDetails['billing_model']
  billing_channel: BillingDetails['channel']
  billing_processing: BillingDetails['billing_processing']
  billing_partner_selling: BillingDetails['partner_selling']
}

export const getBillingThirdPartyIntegrationContext = (
  data: BillingDetails,
): ThirdPartyIntegrationContext => ({
  billing_model: data.billing_model,
  billing_channel: data.channel,
  billing_processing: data.billing_processing,
  billing_partner_selling: data.partner_selling,
})

export function isCreditCardCustomer({ payment_method }: BillingDetails): boolean {
  return payment_method === 'credit_card'
}

export function isPurchaseOrderCustomer({ payment_method }: BillingDetails): boolean {
  return payment_method === 'purchase_order'
}

export function isPrepaidAccount(billingDetails: BillingDetails): boolean {
  return isPrepaidConsumptionCustomer(billingDetails) || isAnnualAndOverages(billingDetails)
}

export function isEligibleForRetroactiveBilling(billingDetails?: BillingDetails): boolean {
  return (
    billingDetails?.billing_model === 'monthly' &&
    billingDetails?.billing_processing === 'biller_v1'
  )
}

export function isEligibleForMPConversion(billingDetails?: BillingDetails): boolean {
  return billingDetails?.channel === 'direct'
}

export function isEligibleForDirectConversion(billingDetails?: BillingDetails): boolean {
  return !!billingDetails && isMarketplaceCustomer(billingDetails)
}
